// Risk Types and Interfaces for JavaScript
// Risk Association System - Phase 2

// Risk Types
export const RISK_TYPES = {
  INFECTION_TRANSMISSION: "Infection Transmission",
  DEVICE_CONTAMINATION: "Device Contamination", 
  ENVIRONMENTAL_CONTAMINATION: "Environmental Contamination",
  CROSS_CONTAMINATION: "Cross-contamination",
  MEDICATION_ERROR: "Medication Error",
  PATIENT_HARM: "Patient Harm",
  STAFF_EXPOSURE: "Staff Exposure"
};

// Risk level: standard (linked to process) or protocol (linked to protocol/step/action)
// QMS Phase 2: unified registry supports both
export const RISK_LEVEL = {
  STANDARD: 'standard',
  PROTOCOL: 'protocol'
};

// Criticality Levels
export const CRITICALITY_LEVELS = {
  CRITICAL: "Critical",
  HIGH: "High",
  MEDIUM: "Medium", 
  LOW: "Low"
};

// Risk Status
export const RISK_STATUS = {
  ACTIVE: "Active",
  UNDER_REVIEW: "Under Review",
  DEPRECATED: "Deprecated"
};

// Default Risk Data Structure
export const createDefaultRisk = (protocolId = null, protocolName = null, stepId = null, actionId = null) => {
  return {
    // IDENTIFICATION
    id: null,
    
    // LEVEL: standard (process) or protocol (protocol/step/action) - QMS Phase 2
    riskLevel: protocolId ? 'protocol' : 'standard',
    organizationId: null,
    processId: null,
    processName: null,
    processCode: null,
    // PROTOCOL HIERARCHY (optional - for contextual association)
    protocolId: protocolId, // Can be null for standalone risks
    protocolName: protocolName, // Can be null for standalone risks
    stepId: stepId,
    stepName: null,
    stepSequence: null,
    
    actionId: actionId,
    actionName: null,
    actionSequence: null,
    
    // RISK SCENARIO
    riskScenario: '',
    riskDescription: '',
    riskType: '', // Will be set from nomenclature
    
    // BIOLOGICAL ASSOCIATIONS
    biological_entities: [],
    
    // RPN CALCULATION
    severity: 5,
    severityRationale: '',
    
    occurrence: 5,
    occurrenceRationale: '',
    
    detection: 5,
    detectionRationale: '',
    
    rpn: 125, // 5 × 5 × 5
    
    // RELEVANT RISK FACTORS (QMS Phase 2 - link to patient/environmental factors)
    applicablePatientRiskFactorIds: [],
    applicableEnvironmentalRiskFactorIds: [],
    // CONTEXTUAL FACTORS
    contextualFactors: [],
    
    // IMPACT
    potentialConsequences: [],
    affectedPopulations: [],
    timeToImpact: {
      min: 1,
      max: 24,
      unit: 'hours'
    },
    
    // MITIGATION
    mitigationStrategies: [],
    alternativeActions: [],
    preventiveControls: [],
    detectiveControls: [],
    correctiveActions: [],
    
    // CRITICALITY
    criticality: CRITICALITY_LEVELS.MEDIUM,
    requiresImmediateAction: false,
    escalationRequired: false,
    escalationPath: [],
    
    // EVIDENCE
    evidenceBase: {
      guidelines: [],
      incidents: [],
      studies: []
    },
    
    // VALIDATION
    validatedBy: '',
    validationDate: null,
    reviewFrequency: 'quarterly',
    nextReviewDate: null,
    
    // AUDIT
    createdAt: null,
    createdBy: '',
    updatedAt: null,
    updatedBy: '',
    status: RISK_STATUS.ACTIVE
  };
};

// Biological Entity Association
export const createBiologicalEntityAssociation = (entityId, entityName, relevance = 'Primary') => {
  return {
    biologicalEntityId: entityId,
    biologicalEntityName: entityName,
    relevance: relevance,
    transmissionLikelihood: 7,
    notes: ''
  };
};

// Contextual Factor
export const createContextualFactor = (factor, type, multiplier = 1.0) => {
  return {
    factor: factor,
    type: type, // 'patient', 'environment', 'temporal', 'equipment'
    rpnMultiplier: multiplier,
    description: '',
    conditions: []
  };
};

// Reference
export const createReference = (type, title, source) => {
  return {
    type: type, // 'Guideline', 'Study', 'Policy', 'Incident Report'
    title: title,
    source: source,
    url: '',
    date: new Date().toISOString()
  };
};

// RPN Calculation Helper
export const calculateRPN = (severity, occurrence, detection) => {
  return severity * occurrence * detection;
};

// Criticality from RPN
export const getCriticalityFromRPN = (rpn) => {
  if (rpn > 200) return CRITICALITY_LEVELS.CRITICAL;
  if (rpn > 100) return CRITICALITY_LEVELS.HIGH;
  if (rpn > 50) return CRITICALITY_LEVELS.MEDIUM;
  return CRITICALITY_LEVELS.LOW;
};

// Risk Type Options for UI
export const RISK_TYPE_OPTIONS = [
  { value: RISK_TYPES.INFECTION_TRANSMISSION, label: 'Infection Transmission' },
  { value: RISK_TYPES.DEVICE_CONTAMINATION, label: 'Device Contamination' },
  { value: RISK_TYPES.ENVIRONMENTAL_CONTAMINATION, label: 'Environmental Contamination' },
  { value: RISK_TYPES.CROSS_CONTAMINATION, label: 'Cross-contamination' },
  { value: RISK_TYPES.MEDICATION_ERROR, label: 'Medication Error' },
  { value: RISK_TYPES.PATIENT_HARM, label: 'Patient Harm' },
  { value: RISK_TYPES.STAFF_EXPOSURE, label: 'Staff Exposure' }
];

// Criticality Options for UI
export const CRITICALITY_OPTIONS = [
  { value: CRITICALITY_LEVELS.LOW, label: 'Low (RPN 1-50)', color: 'green' },
  { value: CRITICALITY_LEVELS.MEDIUM, label: 'Medium (RPN 51-100)', color: 'yellow' },
  { value: CRITICALITY_LEVELS.HIGH, label: 'High (RPN 101-200)', color: 'orange' },
  { value: CRITICALITY_LEVELS.CRITICAL, label: 'Critical (RPN >200)', color: 'red' }
];

// Note: Risk level options removed - risks are now standalone entities

// ============== PHASE 4: ENHANCED RISK ASSESSMENT ==============

// Comprehensive Risk Levels (Phase 4)
export const COMPREHENSIVE_RISK_LEVELS = {
  CRITICAL: 'CRITICAL',    // >= 1000
  HIGH: 'HIGH',            // >= 500
  MEDIUM: 'MEDIUM',        // >= 200
  LOW: 'LOW',              // >= 100
  MINIMAL: 'MINIMAL'       // < 100
};

// Risk Level Configuration
export const RISK_LEVEL_CONFIG = {
  CRITICAL: {
    label: 'Critical',
    threshold: 1000,
    color: '#c62828',
    bgColor: '#ffebee',
    icon: '🚨',
    description: 'Immediate action required - Critical risk to patient safety'
  },
  HIGH: {
    label: 'High',
    threshold: 500,
    color: '#f44336',
    bgColor: '#ffcdd2',
    icon: '⚠️',
    description: 'High priority - Enhanced protocols required'
  },
  MEDIUM: {
    label: 'Medium',
    threshold: 200,
    color: '#ff9800',
    bgColor: '#ffe0b2',
    icon: '⚡',
    description: 'Moderate risk - Standard protocols with vigilance'
  },
  LOW: {
    label: 'Low',
    threshold: 100,
    color: '#ffc107',
    bgColor: '#fff9c4',
    icon: '📊',
    description: 'Low risk - Routine infection prevention measures'
  },
  MINIMAL: {
    label: 'Minimal',
    threshold: 0,
    color: '#4caf50',
    bgColor: '#c8e6c9',
    icon: '✓',
    description: 'Minimal risk - Standard precautions sufficient'
  }
};

// Recommendation Types
export const RECOMMENDATION_TYPES = {
  IMMEDIATE: 'IMMEDIATE',
  PROTOCOL: 'PROTOCOL',
  PATIENT: 'PATIENT',
  ENVIRONMENTAL: 'ENVIRONMENTAL',
  BIOLOGICAL: 'BIOLOGICAL',
  LOCATION: 'LOCATION',
  MONITORING: 'MONITORING',
  STANDARD: 'STANDARD'
};

// Recommendation Priority
export const RECOMMENDATION_PRIORITY = {
  CRITICAL: 'CRITICAL',
  HIGH: 'HIGH',
  MEDIUM: 'MEDIUM',
  LOW: 'LOW'
};

// Get risk level configuration
export const getRiskLevelConfig = (riskLevel) => {
  return RISK_LEVEL_CONFIG[riskLevel] || RISK_LEVEL_CONFIG.MINIMAL;
};

// Get risk level from score
export const getRiskLevelFromScore = (score) => {
  if (score >= 1000) return COMPREHENSIVE_RISK_LEVELS.CRITICAL;
  if (score >= 500) return COMPREHENSIVE_RISK_LEVELS.HIGH;
  if (score >= 200) return COMPREHENSIVE_RISK_LEVELS.MEDIUM;
  if (score >= 100) return COMPREHENSIVE_RISK_LEVELS.LOW;
  return COMPREHENSIVE_RISK_LEVELS.MINIMAL;
};

// Format risk score for display
export const formatRiskScore = (score) => {
  if (score >= 1000) return `${(score / 1000).toFixed(1)}K`;
  return score.toString();
};

// Get multiplier color based on value
export const getMultiplierColor = (multiplier, maxMultiplier = 3.0) => {
  const percentage = (multiplier - 1.0) / (maxMultiplier - 1.0);
  
  if (percentage >= 0.75) return '#c62828'; // Dark red
  if (percentage >= 0.50) return '#f44336'; // Red
  if (percentage >= 0.25) return '#ff9800'; // Orange
  return '#ffc107'; // Yellow
};

// ============== QMS PHASE 2: RISK CONTROLS ==============

/** Risk control type (nomenclature) */
export const RISK_CONTROL_TYPES = {
  PROTOCOL: 'Protocol',
  MONITORING: 'Monitoring',
  TRAINING: 'Training',
  EQUIPMENT: 'Equipment',
  POLICY: 'Policy'
};

/** Control status */
export const RISK_CONTROL_STATUS = {
  ACTIVE: 'Active',
  INEFFECTIVE: 'Ineffective',
  UNDER_REVIEW: 'Under Review'
};

export const RISK_CONTROL_TYPE_OPTIONS = [
  { value: RISK_CONTROL_TYPES.PROTOCOL, label: 'Protocol' },
  { value: RISK_CONTROL_TYPES.MONITORING, label: 'Monitoring' },
  { value: RISK_CONTROL_TYPES.TRAINING, label: 'Training' },
  { value: RISK_CONTROL_TYPES.EQUIPMENT, label: 'Equipment' },
  { value: RISK_CONTROL_TYPES.POLICY, label: 'Policy' }
];

export const RISK_CONTROL_STATUS_OPTIONS = [
  { value: RISK_CONTROL_STATUS.ACTIVE, label: 'Active' },
  { value: RISK_CONTROL_STATUS.INEFFECTIVE, label: 'Ineffective' },
  { value: RISK_CONTROL_STATUS.UNDER_REVIEW, label: 'Under Review' }
];

// ============== QMS PHASE 2: RISK THRESHOLDS ==============

export const THRESHOLD_OPERATORS = {
  LT: '<',
  LTE: '<=',
  EQ: '=',
  GTE: '>=',
  GT: '>'
};

export const THRESHOLD_METRICS = {
  COMPLIANCE_PCT: 'compliance_pct',
  RPN: 'rpn',
  INCIDENT_COUNT: 'incident_count'
};

export const THRESHOLD_OPERATOR_OPTIONS = [
  { value: THRESHOLD_OPERATORS.LT, label: '<' },
  { value: THRESHOLD_OPERATORS.LTE, label: '<=' },
  { value: THRESHOLD_OPERATORS.EQ, label: '=' },
  { value: THRESHOLD_OPERATORS.GTE, label: '>=' },
  { value: THRESHOLD_OPERATORS.GT, label: '>' }
];

export const THRESHOLD_METRIC_OPTIONS = [
  { value: THRESHOLD_METRICS.COMPLIANCE_PCT, label: 'Compliance %' },
  { value: THRESHOLD_METRICS.RPN, label: 'RPN' },
  { value: THRESHOLD_METRICS.INCIDENT_COUNT, label: 'Incident count' }
];
